import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { ArrowLeft, Pencil, Loader2 } from 'lucide-react'
import matchService, {
  type AdminMatchListItem,
  type AdminMatchListResponse,
} from '../services/matchService'
import formula1Service from '../services/formula1Service'
import futureChampionService from '../services/futureChampionService'
import topScorerService from '../services/topScorerService'
import type { Formula1Race } from '../types/formula1'
import { pickPastFormula1Races, pickUpcomingFormula1Races } from '../utils/formula1RaceTiming'
import type { FutureChampionEvent } from '../types/futureChampion'
import type { TopScorerEvent } from '../types/topScorer'
import { isAdminUser } from '../utils/admin'
import { getMatchDate } from '../utils/dateHelper'
import { footballDisplayScore, footballLiveBadge, FOOTBALL_LIVE_POLL_MS, isFootballMatchLive } from '../utils/footballLiveScore'
import { ManageMatchModal } from '../components/admin/ManageMatchModal'
import { ManageRaceModal } from '../components/admin/ManageRaceModal'
import { ManageChoiceResultModal } from '../components/admin/ManageChoiceResultModal'

type TabKind = 'football' | 'formula1' | 'futureChampion' | 'topScorer'

const TAB_CLASS = (active: boolean) =>
  `px-3 sm:px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap ${
    active ? 'bg-primary text-primary-foreground' : 'bg-muted/30 text-foreground hover:bg-muted/50'
  }`

const AdminUpdateResults: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [activeTab, setActiveTab] = useState<TabKind>('football')

  const [matches, setMatches] = useState<AdminMatchListItem[]>([])
  const [matchesLoading, setMatchesLoading] = useState(false)
  const [matchesPage, setMatchesPage] = useState(1)
  const [matchesTotalPages, setMatchesTotalPages] = useState(1)
  const [footballListTab, setFootballListTab] = useState<'past' | 'upcoming'>('past')
  const [footballSearch, setFootballSearch] = useState('')
  const [footballPollTick, setFootballPollTick] = useState(0)
  const [matchModalOpen, setMatchModalOpen] = useState(false)
  const [selectedMatch, setSelectedMatch] = useState<AdminMatchListItem | null>(null)

  const [races, setRaces] = useState<Formula1Race[]>([])
  const [racesLoading, setRacesLoading] = useState(false)
  const [f1ListTab, setF1ListTab] = useState<'past' | 'upcoming'>('past')
  const [raceModalOpen, setRaceModalOpen] = useState(false)
  const [selectedRace, setSelectedRace] = useState<Formula1Race | null>(null)

  const [fcEvents, setFcEvents] = useState<FutureChampionEvent[]>([])
  const [fcLoading, setFcLoading] = useState(false)
  const [fcModalOpen, setFcModalOpen] = useState(false)
  const [selectedFc, setSelectedFc] = useState<FutureChampionEvent | null>(null)

  const [tsEvents, setTsEvents] = useState<TopScorerEvent[]>([])
  const [tsLoading, setTsLoading] = useState(false)
  const [tsModalOpen, setTsModalOpen] = useState(false)
  const [selectedTs, setSelectedTs] = useState<TopScorerEvent | null>(null)

  useEffect(() => {
    if (!isAdminUser(user)) {
      navigate('/dashboard', { replace: true })
      return
    }
  }, [user, navigate])

  useEffect(() => {
    setMatchesPage(1)
  }, [footballListTab, footballSearch])

  useEffect(() => {
    if (!isAdminUser(user) || activeTab !== 'football') return
    const id = window.setInterval(() => setFootballPollTick((n) => n + 1), FOOTBALL_LIVE_POLL_MS)
    return () => window.clearInterval(id)
  }, [user, activeTab])

  useEffect(() => {
    if (!isAdminUser(user)) return
    if (activeTab !== 'football') return
    const silent = footballPollTick > 0 && matchesPage === 1 && matches.length > 0
    const handle = window.setTimeout(() => {
      if (!silent) setMatchesLoading(true)
      const q = footballSearch.trim()
      matchService
        .getAllMatches({
          page: matchesPage,
          limit: 20,
          search: q || undefined,
          ...(q ? {} : { scope: footballListTab }),
        })
        .then((res: AdminMatchListResponse) => {
          const list = res.matches ?? []
          const now = Date.now()
          const scoped = q
            ? list
            : list.filter((m) => {
                const kickoff = new Date(getMatchDate(m, true)).getTime()
                if (Number.isNaN(kickoff)) return false
                return footballListTab === 'upcoming' ? kickoff > now : kickoff <= now
              })
          setMatches((prev) => (matchesPage === 1 ? scoped : [...prev, ...scoped]))
          setMatchesTotalPages(res.totalPages ?? 1)
        })
        .catch(() => {
          if (matchesPage === 1) setMatches([])
        })
        .finally(() => setMatchesLoading(false))
    }, 400)
    return () => window.clearTimeout(handle)
  }, [user, activeTab, matchesPage, footballListTab, footballSearch, footballPollTick])

  useEffect(() => {
    if (!isAdminUser(user)) return
    if (activeTab === 'formula1') {
      setRacesLoading(true)
      formula1Service
        .getRaces({ limit: 50 })
        .then((list) => setRaces(Array.isArray(list) ? list : []))
        .catch(() => setRaces([]))
        .finally(() => setRacesLoading(false))
    }
  }, [user, activeTab])

  useEffect(() => {
    if (!isAdminUser(user)) return
    if (activeTab === 'futureChampion') {
      setFcLoading(true)
      futureChampionService
        .getEvents({ upcomingOnly: false })
        .then((list) => setFcEvents(Array.isArray(list) ? list : []))
        .catch(() => setFcEvents([]))
        .finally(() => setFcLoading(false))
    }
  }, [user, activeTab])

  useEffect(() => {
    if (!isAdminUser(user)) return
    if (activeTab === 'topScorer') {
      setTsLoading(true)
      topScorerService
        .getEvents({ upcomingOnly: false })
        .then((list) => setTsEvents(Array.isArray(list) ? list : []))
        .catch(() => setTsEvents([]))
        .finally(() => setTsLoading(false))
    }
  }, [user, activeTab])

  if (!user) return null
  if (!isAdminUser(user)) return null

  const pastRaces = pickPastFormula1Races(races)
  const upcomingRaces = pickUpcomingFormula1Races(races)
  const visibleRaces = f1ListTab === 'past' ? pastRaces : upcomingRaces

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-4 py-3 flex items-center gap-4">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm font-medium">
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </Link>
        <h1 className="text-lg font-semibold text-foreground">Update results (Admin)</h1>
      </header>
      <main className="container max-w-2xl mx-auto p-4">
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          <button type="button" onClick={() => setActiveTab('football')} className={TAB_CLASS(activeTab === 'football')}>
            Football
          </button>
          <button type="button" onClick={() => setActiveTab('formula1')} className={TAB_CLASS(activeTab === 'formula1')}>
            Formula 1
          </button>
          <button type="button" onClick={() => setActiveTab('futureChampion')} className={TAB_CLASS(activeTab === 'futureChampion')}>
            Future Champion
          </button>
          <button type="button" onClick={() => setActiveTab('topScorer')} className={TAB_CLASS(activeTab === 'topScorer')}>
            Top Scorer
          </button>
        </div>

        {activeTab === 'football' && (
          <div className="space-y-4">
            <div className="flex gap-1 p-1 rounded-xl bg-muted/30">
              <button
                type="button"
                onClick={() => setFootballListTab('past')}
                className={TAB_CLASS(footballListTab === 'past' && !footballSearch.trim()) + ' flex-1'}
              >
                Past
              </button>
              <button
                type="button"
                onClick={() => setFootballListTab('upcoming')}
                className={TAB_CLASS(footballListTab === 'upcoming' && !footballSearch.trim()) + ' flex-1'}
              >
                Upcoming
              </button>
            </div>
            <input
              type="search"
              value={footballSearch}
              onChange={(e) => setFootballSearch(e.target.value)}
              placeholder="Search team or league..."
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
            />
            {footballSearch.trim() ? (
              <p className="text-xs text-muted-foreground">Searching all matches</p>
            ) : null}
            {matchesLoading && matchesPage === 1 && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
            {!matchesLoading || matchesPage > 1 ? matches.map((m) => (
              <div key={m.id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-foreground">
                    {m.teamA} – {m.teamB}
                    {footballDisplayScore(m) ? ` (${footballDisplayScore(m)})` : ''}
                    {isFootballMatchLive(m) ? ` · ${footballLiveBadge(m)}` : ''}
                  </p>
                  <p className="text-sm text-muted-foreground">{m.league} · {m.date} {m.time}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedMatch(m); setMatchModalOpen(true) }}
                  className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30"
                  aria-label="Edit match"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )) : null}
            {!matchesLoading && matches.length === 0 && (
              <p className="text-muted-foreground py-6 text-center">
                {footballSearch.trim()
                  ? 'No matches found'
                  : footballListTab === 'past'
                    ? 'No past matches'
                    : 'No upcoming matches'}
              </p>
            )}
            {!matchesLoading && matches.length > 0 && matchesPage < matchesTotalPages && (
              <button
                type="button"
                onClick={() => setMatchesPage((p) => p + 1)}
                className="w-full py-2 rounded-xl border border-border hover:bg-muted/30 text-sm font-medium"
              >
                Load more
              </button>
            )}
          </div>
        )}

        {activeTab === 'formula1' && (
          <div className="space-y-4">
            <div className="flex gap-1 p-1 rounded-xl bg-muted/30">
              <button
                type="button"
                onClick={() => setF1ListTab('past')}
                className={TAB_CLASS(f1ListTab === 'past') + ' flex-1'}
              >
                Past ({pastRaces.length})
              </button>
              <button
                type="button"
                onClick={() => setF1ListTab('upcoming')}
                className={TAB_CLASS(f1ListTab === 'upcoming') + ' flex-1'}
              >
                Upcoming ({upcomingRaces.length})
              </button>
            </div>
            {racesLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
            {!racesLoading && visibleRaces.map((r) => (
              <div key={r.id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-foreground">
                    Race {r.day}: {r.city}{r.country ? `, ${r.country}` : ''}
                  </p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {r.status}
                    {r.actualResults && Object.keys(r.actualResults).length > 0
                      ? ` · ${Object.keys(r.actualResults).length} results`
                      : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedRace(r); setRaceModalOpen(true) }}
                  className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30"
                  aria-label="Edit race results"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            ))}
            {!racesLoading && visibleRaces.length === 0 && (
              <p className="text-muted-foreground py-6 text-center">
                {f1ListTab === 'past' ? 'No past races' : 'No upcoming races'}
              </p>
            )}
          </div>
        )}

        {activeTab === 'futureChampion' && (
          <div className="space-y-4">
            {fcLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
            {!fcLoading && fcEvents.map((ev) => (
              <div key={ev.id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">{ev.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {ev.tournamentName || ev.gameType}
                    {ev.eventDate ? ` · ${new Date(ev.eventDate).toLocaleDateString()}` : ''}
                    {ev.actualChampion ? ` · Winner: ${ev.actualChampion}` : ' · No result yet'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedFc(ev); setFcModalOpen(true) }}
                  className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 shrink-0"
                  aria-label="Set future champion result"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            ))}
            {!fcLoading && fcEvents.length === 0 && (
              <p className="text-muted-foreground py-6 text-center">No future champion events</p>
            )}
          </div>
        )}

        {activeTab === 'topScorer' && (
          <div className="space-y-4">
            {tsLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
            {!tsLoading && tsEvents.map((ev) => (
              <div key={ev.id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">{ev.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {ev.league}
                    {ev.eventDate ? ` · ${new Date(ev.eventDate).toLocaleDateString()}` : ''}
                    {ev.actualTopScorer ? ` · Scorer: ${ev.actualTopScorer}` : ' · No result yet'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedTs(ev); setTsModalOpen(true) }}
                  className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 shrink-0"
                  aria-label="Set top scorer result"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            ))}
            {!tsLoading && tsEvents.length === 0 && (
              <p className="text-muted-foreground py-6 text-center">No top scorer events</p>
            )}
          </div>
        )}
      </main>

      <ManageMatchModal
        open={matchModalOpen}
        onOpenChange={setMatchModalOpen}
        match={selectedMatch}
        onSaved={(updated) => {
          setMatches((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)))
        }}
        onSave={(id, data) => matchService.updateMatch(id, data)}
      />
      <ManageRaceModal
        open={raceModalOpen}
        onOpenChange={setRaceModalOpen}
        race={selectedRace}
        onSaved={(updated) => {
          setRaces((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
        }}
        onSave={(raceId, data) => formula1Service.updateRace(raceId, { actualResults: data.actualResults, status: data.status as 'completed' })}
      />
      <ManageChoiceResultModal
        open={fcModalOpen}
        onOpenChange={setFcModalOpen}
        title={selectedFc?.name || 'Future Champion'}
        subtitle={selectedFc?.tournamentName || selectedFc?.gameType}
        choices={selectedFc?.choices ?? []}
        currentValue={selectedFc?.actualChampion}
        resultLabel="Actual champion"
        onSave={async (actualChampion) => {
          if (!selectedFc?.id) return
          const updated = await futureChampionService.updateEvent(selectedFc.id, { actualChampion })
          setFcEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)))
          setSelectedFc(updated)
        }}
      />
      <ManageChoiceResultModal
        open={tsModalOpen}
        onOpenChange={setTsModalOpen}
        title={selectedTs?.name || 'Top Scorer'}
        subtitle={selectedTs?.league}
        choices={selectedTs?.choices ?? []}
        currentValue={selectedTs?.actualTopScorer}
        resultLabel="Actual top scorer"
        onSave={async (actualTopScorer) => {
          if (!selectedTs?.id) return
          const updated = await topScorerService.updateEvent(selectedTs.id, { actualTopScorer })
          setTsEvents((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)))
          setSelectedTs(updated)
        }}
      />
    </div>
  )
}

export default AdminUpdateResults
